import { useLayoutEffect, useState } from "react";

/**
 * Asks whether the element's content fits. Rechecked whenever it resizes, which
 * covers column resizing as well as columns being hidden or shown.
 *
 * The element is tracked as state through a callback ref so that a re-mounted
 * element is observed anew. A plain ref would leave the observer on the detached
 * element, whose final zero-size notification would report "fits" forever.
 *
 * In a layout effect rather than a plain one so the result is there on the first
 * paint. Deferring it to `useEffect` would flash on every load and every resize.
 */
function useIsClamped(): {
    ref: (element: HTMLElement | null) => void;
    isClamped: boolean;
} {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const [isClamped, setIsClamped] = useState(false);

    useLayoutEffect(() => {
        if (!element) {
            return;
        }
        // scrollHeight is the height of the content
        // clientHeight is the height of the container -> the clamped lines
        const check = () =>
            setIsClamped(element.scrollHeight > element.clientHeight);

        check();
        const observer = new ResizeObserver(check);
        observer.observe(element);
        return () => observer.disconnect();
    }, [element]);

    return { ref: setElement, isClamped };
}

export { useIsClamped };
